/**
 * Error boundary component.
 * Catches React errors and displays a Vuexy-styled error message.
 * Based on Vuexy error handling patterns.
 */

// React Imports
import { Component, type ReactNode } from 'react';

// MUI Imports
import { Box, Typography, Button, Card, CardContent } from '@mui/material';

// Component Imports
import { AlertCircle } from '@core/components/icons';

/**
 * Error boundary props
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Error boundary state
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch React errors.
 * Displays a Vuexy-styled error message when an error occurs.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            backgroundColor: 'background.default',
          }}
        >
          <Card
            sx={{
              maxWidth: 600,
              width: '100%',
              boxShadow: 'var(--mui-customShadows-lg)',
            }}
          >
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  mb: 4,
                  fontSize: '5.5rem',
                  color: 'error.main',
                  lineHeight: 1,
                }}
              >
                <AlertCircle size={88} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Something went wrong
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.setState({ hasError: false, error: null })}
                sx={{
                  textTransform: 'none',
                  px: 4,
                }}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}
